import { useEffect } from 'react';
import { useTradeDetail, type useUpdatePosition } from '@/hooks/trades';
import { useToastStore } from '@/stores';
import { AmendTradeModal } from './trade-detail/modals/AmendTradeModal';

interface AmendTradeModalConnectorProps {
  /** Selected pending trade's id — resolved to a full PositionDetail before mounting. */
  tradeId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Page-owned `useUpdatePosition` handle, forwarded to the modal. */
  updatePosition: ReturnType<typeof useUpdatePosition>;
}

/**
 * LR-484: the Trades list page only holds a light `TradeItem`, but `AmendTradeModal`
 * needs the full `PositionDetail` (counterparty_name, sedol, decimal-string money,
 * etc. — a `TradeItem` would seed the form with wrong/empty values). This thin
 * connector resolves `tradeId → PositionDetail` via `useTradeDetail` and mounts the
 * modal only once the detail is available, keeping the conditional query out of
 * `TradesPage`'s body (rules-of-hooks clean). Mirrors how `TradeDetailPage` mounts
 * the modal only when its `useTradeDetail` data exists.
 *
 * amend is the ONLY FAB action that needs a network fetch before any UI appears, so a
 * failed/slow detail fetch must not silently dead-end (LR-484 r1 M2): on `isError` we
 * surface a toast and close, instead of returning null forever with no feedback.
 */
export function AmendTradeModalConnector({
  tradeId,
  isOpen,
  onClose,
  updatePosition,
}: AmendTradeModalConnectorProps) {
  const { data: trade, isError } = useTradeDetail(tradeId, isOpen);
  const { addToast } = useToastStore();

  useEffect(() => {
    if (isOpen && isError) {
      addToast('error', 'Could not load the trade to amend — please try again.');
      onClose();
    }
  }, [isOpen, isError, addToast, onClose]);

  if (!isOpen || !trade) return null;
  return (
    <AmendTradeModal isOpen onClose={onClose} trade={trade} updatePosition={updatePosition} />
  );
}
